import { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import axios from "axios";
import { BASE_URL } from "../../../config/axios";

const SectionFilter = ({ onApply, sectionData = [] }) => {
  const [filters, setFilters] = useState({
    project: "",
    section: "",
    location: "",
    minLength: "",
    maxLength: "",
    minCost: "",
    maxCost: "",
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedRangeType, setSelectedRangeType] = useState(null);

  const [projectList, setProjectList] = useState([]);
  const [sectionList, setSectionList] = useState([]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/dpr/projectdetails/`);
      setProjectList(res.data);
    } catch (err) {
      console.error("Dropdown error:", err);
    }
  };

  const projectOptions = projectList.map((i) => ({
    value: i.id,
    label: i.project_name,
  }));

  const sectionOptions = sectionList.map((i) => ({
    value: i.id,
    label: i.section_name,
  }));

  // ✅ Extract unique locations from the section data passed as prop
  const locationOptions = useMemo(() => {
    const unique = [...new Set(
      sectionData
        .map((i) => i.location)
        .filter((loc) => loc && loc.trim() !== "")
    )];
    return unique.map((loc) => ({ value: loc, label: loc }));
  }, [sectionData]);

  const handleProjectChange = async (opt) => {
    setSelectedProject(opt);
    setSelectedSection(null);
    setFilters((prev) => ({ ...prev, project: opt ? opt.value : "", section: "" }));

    if (!opt) {
      setSectionList([]);
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/dpr/sectionfilter/${opt.value}/`);
      setSectionList(res.data);
    } catch (err) {
      console.error("Section fetch error:", err);
      setSectionList([]);
    }
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const safe = (val) => (val !== "" && val !== null && val !== undefined ? val : "null");

  const handleApply = () => {
    const formattedFilters = {
      project: safe(filters.project),
      section: safe(filters.section),
      location: safe(filters.location),
      workStage: "null",
      minLength: safe(filters.minLength),
      maxLength: safe(filters.maxLength),
      minCost: safe(filters.minCost),
      maxCost: safe(filters.maxCost),
    };
    console.log("🔥 SECTION FILTER APPLY:", formattedFilters);
    onApply(formattedFilters);
  };

  const handleClear = () => {
    setFilters({
      project: "",
      section: "",
      location: "",
      minLength: "",
      maxLength: "",
      minCost: "",
      maxCost: "",
    });
    setSelectedProject(null);
    setSelectedSection(null);
    setSelectedLocation(null);
    setSelectedRangeType(null);
    setSectionList([]);
    onApply({
      project: "null",
      section: "null",
      location: "null",
      workStage: "null",
      minLength: "null",
      maxLength: "null",
      minCost: "null",
      maxCost: "null",
    });
  };

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (base) => ({ ...base, minWidth: 110, maxWidth: 260, width: "auto", flex: "1 1 auto" }),
    control: (base) => ({ ...base, minHeight: 32, height: "auto", border: "none", borderRadius: 0, boxShadow: "none", background: "transparent", cursor: "pointer", flexWrap: "wrap" }),
    valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: "#111827", whiteSpace: "normal", wordBreak: "break-word", maxWidth: "100%" }),
    placeholder: (base) => ({ ...base, fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }),
    input: (base) => ({ ...base, fontSize: 12, margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px", color: "#9ca3af" }),
    clearIndicator: (base) => ({ ...base, padding: "0 4px", color: "#9ca3af" }),
    menu: (base) => ({ ...base, fontSize: 12, minWidth: 160, width: "max-content", maxWidth: 320 }),
    option: (base, state) => ({
      ...base, fontSize: 12, padding: "7px 12px",
      backgroundColor: state.isSelected ? "#1e40af" : state.isFocused ? "#f3f4f6" : "#fff",
      color: state.isSelected ? "#fff" : "#111827",
      whiteSpace: "normal", wordBreak: "break-word", cursor: "pointer",
    }),
  };

  const iconStyle = { flexShrink: 0, marginTop: 1 };

  return (
    <>
      <style>{`
        .sf-bar { display:flex;flex-wrap:wrap;align-items:stretch;gap:0;border:0.5px solid #d1d5db;border-radius:8px;overflow:visible;background:#fff;margin-bottom:14px; }
        .sf-chip { display:flex;align-items:center;gap:6px;padding:0 12px;min-height:38px;height:auto;border-right:0.5px solid #e5e7eb;flex:1 1 auto;min-width:110px;max-width:260px;position:relative;flex-wrap:wrap; }
        .sf-chip:last-of-type { border-right:none; }
        .sf-chip-range { display:flex;align-items:center;gap:6px;padding:0 12px;min-height:38px;height:auto;border-right:0.5px solid #e5e7eb;flex:1 1 200px;flex-wrap:wrap; }
        .sf-chip-range.disabled { opacity:0.45;pointer-events:none; }
        .sf-range-label { font-size:11px;color:#9ca3af;white-space:nowrap;flex-shrink:0; }
        .sf-range-input { font-size:11px;border:none;outline:none;background:transparent;color:#111827;min-width:80px;max-width:120px;flex:1 1 auto;padding:4px 0;font-family:inherit; }
        .sf-range-input:disabled { color:#9ca3af;cursor:not-allowed; }
        .sf-range-input::placeholder { color:#d1d5db; }
        .sf-sep { font-size:11px;color:#d1d5db;flex-shrink:0; }
        .sf-btn-apply { height:28px;padding:0 14px;background:#1e40af;color:#fff;border:none;border-radius:5px;font-size:12px;font-weight:500;cursor:pointer;white-space:nowrap;line-height:1; }
        .sf-btn-apply:hover { background:#1d4ed8; }
        .sf-btn-clear { height:28px;padding:0 10px;background:transparent;color:#6b7280;border:0.5px solid #e5e7eb;border-radius:5px;font-size:12px;cursor:pointer;white-space:nowrap;line-height:1; }
        .sf-btn-clear:hover { background:#f9fafb; }
        @media (max-width:900px) {
          .sf-bar { flex-wrap:wrap; }
          .sf-chip,.sf-chip-range { border-right:none;border-bottom:0.5px solid #e5e7eb;flex:1 1 160px;max-width:100%; }
        }
      `}</style>

      <div className="sf-bar">

        {/* PROJECT */}
        <div className="sf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3a76bd" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          <Select
            styles={selectStyles}
            options={projectOptions}
            placeholder="Project"
            isClearable
            value={selectedProject}
            onChange={handleProjectChange}
            menuPortalTarget={document.body}
          />
        </div>

        {/* SECTION */}
        <div className="sf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 3a4.5 4.5 0 0 1 0 9 4.5 4.5 0 0 1 0-9" />
            <path d="M3.6 15h16.8" />
          </svg>
          <Select
            styles={{
              ...selectStyles,
              control: (base) => ({ ...selectStyles.control(base), opacity: !filters.project ? 0.5 : 1 }),
            }}
            options={sectionOptions}
            placeholder="Section"
            isClearable
            isDisabled={!filters.project}
            value={selectedSection}
            onChange={(opt) => {
              setSelectedSection(opt);
              setFilters((prev) => ({ ...prev, section: opt ? opt.value : "" }));
            }}
            menuPortalTarget={document.body}
          />
        </div>

        {/* LOCATION — ✅ now a dropdown */}
        <div className="sf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <Select
            styles={selectStyles}
            options={locationOptions}
            placeholder="Location"
            isClearable
            value={selectedLocation}
            onChange={(opt) => {
              setSelectedLocation(opt);
              setFilters((prev) => ({ ...prev, location: opt ? opt.value : "" }));
            }}
            menuPortalTarget={document.body}
            noOptionsMessage={() => "No locations available"}
          />
        </div>

        {/* RANGE TYPE */}
        <div className="sf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={selectedRangeType ? "#d97706" : "#9ca3af"} strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <Select
            styles={selectStyles}
            placeholder="Range Type"
            isClearable
            value={selectedRangeType}
            options={[
              { value: "length", label: "Length (KM)" },
              { value: "cost", label: "Cost (Lakhs)" },
            ]}
            onChange={(opt) => {
              setSelectedRangeType(opt || null);
              setFilters((prev) => ({ ...prev, minLength: "", maxLength: "", minCost: "", maxCost: "" }));
            }}
            menuPortalTarget={document.body}
          />
        </div>

        {/* RANGE INPUT */}
        <div className={`sf-chip-range ${!selectedRangeType ? "disabled" : ""}`}>
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={selectedRangeType ? "#d97706" : "#9ca3af"} strokeWidth="2">
            {selectedRangeType?.value === "cost" ? (
              <><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>
            ) : (
              <path d="M3 6h18M3 12h18M3 18h18" />
            )}
          </svg>
          <span className="sf-range-label">
            {selectedRangeType?.value === "length" ? "Length" : selectedRangeType?.value === "cost" ? "Cost" : "Range"}
          </span>
          <input
            className="sf-range-input"
            type="number"
            placeholder="Min"
            disabled={!selectedRangeType}
            value={selectedRangeType?.value === "length" ? filters.minLength : filters.minCost}
            onChange={(e) =>
              handleChange({
                target: {
                  name: selectedRangeType?.value === "length" ? "minLength" : "minCost",
                  value: e.target.value,
                },
              })
            }
          />
          <span className="sf-sep">—</span>
          <input
            className="sf-range-input"
            type="number"
            placeholder="Max"
            disabled={!selectedRangeType}
            value={selectedRangeType?.value === "length" ? filters.maxLength : filters.maxCost}
            onChange={(e) =>
              handleChange({
                target: {
                  name: selectedRangeType?.value === "length" ? "maxLength" : "maxCost",
                  value: e.target.value,
                },
              })
            }
          />
        </div>

        {/* BUTTONS */}
        <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"0 10px", borderLeft:"0.5px solid #e5e7eb", flexShrink:0, minHeight:"38px" }}>
          <button className="sf-btn-apply" onClick={handleApply}>Apply</button>
          <button className="sf-btn-clear" onClick={handleClear}>Clear</button>
        </div>

      </div>
    </>
  );
};

export default SectionFilter;